import moment, { DurationInputArg1, DurationInputArg2, MomentInput } from 'moment';

/**
 * Segéd osztály az időértékek megfelelő tárolásához.
 * 
 * Korábban minden időpontérték Date típusban volt kezelve és tárolva, de igény merült fel, 
 * hogy az alkalmazás működése során mindenhol (log, DB mező, stb.) ember számára olvasható,
 * egységes formában történjen ez. Ennek következménye, hogy a (pl. interfészen) kapcsolódó 
 * alkalmazások fejlesztése során is egységes lehessen az adatkommunkációban az időértékek 
 * formája, és ne kelljen extra (pl. generál) mezőértékekkel bajlódni (pl. interfészen és 
 * kódban ISO szöveg, DB-ben szám).
 * 
 * Ez a megoldás minden időértéket (akár ms pontossággal) ISO időformátumban UTC időzóna 
 * szerint tárolja a 'time' mezőben. Az időérték eredeti időzónájának időeltolását percben 
 * az 'offset' mező tárolja (ennek segítségével visszaállítható az eredeti időérték).
 * 
 * Az UTC zónában való tárolás egyszerűsíti a különböző időpont-értékek összehasonlítását.
 * 
 * Az ISO formátum lehetővé teszi a különböző időértékek szöveges összehasonlítását, így DB
 * lekérés során is alkalmazható a kisebb-nagyobb-egynlő vizsgálat ($lt, $lte, $gt, $gte, $eq, stb.).
 * 
 * Megjegyzés: a tárolás ilyen módon nem igazán optimális, de a fent leírt igénynek ezzel a 
 * módszerrel lehett megfelelni.
 * 
 * ISO időformátum: @see LocalDateTime.isISODateTime
 * ISO UTC időformátum: @see LocalDateTime.isUTCDateTime
 * 
 */
export class LocalDateTime {

    /**
     * Visszaadja, hogy a paraméterben megadott érték elfogadható-e ISO formátumú időpont-értékként. 
     * Érték-helyességet nem vizsgál (pl. 2021.02.31 dátum vagy +14:30 időzóna érvényes-e)!!!
     * 
     * Példa formátumok:
     * ÉÉÉÉ-HH-NNTÓÓ:PP:MP.milZ
     * ÉÉÉÉ-HH-NNTÓÓ:PP:MP.mil+óó:pp
     * 
     * Ahol:
     *  - ÉÉÉÉ: 4 számjegyű év (0000-9999)
     *  - HH: a hónap éven belüli értéke (két számjeggyel, 0-val kiegészítve, ha egy számjegyű, ÉK: 01-12)
     *  - NN: a nap száma a hónapon belül (két számjeggyel, 0-val kiegészítve, ha egy számjegyű, ÉK: 01-31)
     *  - T: idő formátum jelzése
     *  - ÓÓ: az óra napon belüli értéke (24 órás megjelenítéssel, két számjeggyel, 0-val kiegészítve, ha egy számjegyű; ÉK: 00-23)
     *  - PP: a perc órán belüli értéke (két számjeggyel, 0-val kiegészítve, ha egy számjegyű; ÉK: 00-59)
     *  - MP: a másodperc percen belüli értéke (két számjeggyel, 0-val kiegészítve, ha egy számjegyű; 60-as érték csak szökőmásodperc jelölésére!; ÉK: 00-59 és a 60)
     *  - mil: az ezredmásodperc másodpercen belüli értéke (három számjeggyel, 0-val kiegészítve egy és két számjegy esetén; ÉK: 000-999)
     *  - Z: UTC időzóna jelzése (megegyezik a +00:00 és -00:00 időzóna-eltolással; vagy ezt, vagy az időzóna-eltolást meg kell adni)
     *  - +-: az iőzóna elolás előjele
     *  - óó: az időzóna-eltolás óra értéke (0 órától 14 óráig; két számjeggyel, 0-val kiegészítve, ha egy számjegyű; ÉK: 00-14)
     *  - pp: az időzóna-eltolás perc értéke (ÉK: 00, 15, 30 és 45 lehet csak)
     * 
     *  - @see https://en.wikipedia.org/wiki/Time_zone
     *  - @see https://en.wikipedia.org/wiki/UTC_offset
     *  - @see https://en.wikipedia.org/wiki/ISO_8601
     * 
     * Megjegyzés: az ISO formátum leírása (ld. Wikipédia link) alapján több formázási lehetőség 
     * is adott, de a jelenlegi megoldáshoz (és a működés egységesítéséhez) a fentebb leírt formátum-
     * megkötések lettek alkalmazva.
     * 
     * @param value A vizsgálandó érték.
     * @returns true, ha a paraméterben megadott érték formátuma megfelel a fentebb leírt 
     *          szabálynak, egyébként false.
     */
    public static isISODateTime(value: any) {
        return value && typeof value === 'string'
            && (/(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])(T|\s)([0-1][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9]|60)\.\d{3}(Z|[+-](0[0-9]|1[0-4]):(00|15|30|45))/).test(value);
    }

    /**
     * Visszaadja, hogy a paraméterben megadott érték elfogadható-e ISO formátumú UTC időpont-értékként. 
     * Érték-helyességet nem vizsgál (pl. 2021.02.31 dátum)!!!
     * 
     * @see LocalDateTime.isISODateTime
     * 
     * @param value A vizsgálandó érték.
     * @returns True, ha a paraméterben megadott érték formátuma megfelel a regex-nek, egyébként false.
     */
    public static isUTCDateTime(value: any) {
        return value && typeof value === 'string'
            && (/(\d{4})-(0[1-9]|1[0-2])-(0[1-9]|[12][0-9]|3[01])(T|\s)([0-1][0-9]|2[0-3]):([0-5][0-9]):([0-5][0-9]|60)\.\d{3}Z/).test(value);
    }

    /**
     * Visszaadja, hogy a paraméterben megadott értéket lehet-e LocalDateTime inicializáláshoz használni.
     * 
     * @param value A vizsgálandó szöveges érték.
     * @returns True, ha a paraméterben megadott érték megfelel a feltételeknek, egyébként false.
     */
    public static canBeLocalDateTime(value: any) {
        // console.warn("canBeLocalDateTime: " + JSON.stringify(value)
        //     + "\n Date: " + (value instanceof Date)
        //     + "\n number: " + (typeof value === "number")
        //     + "\n isISODateTime: " + (typeof value === "string" && this.isISODateTime(value))
        //     + "\n isMoment: " + (moment.isMoment(value) && (<moment.Moment>value).isValid())
        //     + "\n isLocalDateTime: " + (LocalDateTime.isLocalDateTime(value))
        // );
        return value && (
            value instanceof Date
            || typeof value === "number"
            || (typeof value === "string" && LocalDateTime.isISODateTime(value))
            || (moment.isMoment(value) && (<moment.Moment>value).isValid())
            || LocalDateTime.isLocalDateTime(value)
        );
    }

    /**
     * Visszaadja, hogy a paraméterben megadott értéket LocalDateTime struktúrájú-e
     * (azaz van-e értékelhető time és offset mezője).
     * 
     * @param value 
     * @returns 
     */
    public static isLocalDateTime(value: any) {
        let result: boolean = value
            && typeof value === 'object'
            && 'time' in value
            && 'offset' in value
            && typeof value.time === 'string'
            && typeof value.offset === 'number';
        // console.warn("isLocalDateTime: " + JSON.stringify(value) + " -> " + result);
        return result;
    }

    private time: string = null;
    private offset: number = null;

    /**
     * Az osztály konstruktora. 
     * 
     * A paraméter lehetséges értékei:
     *  - string: 
     *      a, ISO formátumú időpont érték időzóna-eltolás magadással vagy UTC időben.
     *      b, a moment konstruktorának megfelelő formátum
     *          @see https://momentjs.com/docs/#/parsing/string/
     *  - number: epoch timestamp
     *  - Date: javascript Date objektum
     *  - LocalDateTime: érvénye LocalDateTime objektum
     *  - Moment: érvényes Moment objektum
     *  - any: bármi más (JS miatt)
     * 
     * @param value 
     */
    constructor(value: string | number | Date | LocalDateTime | moment.Moment | { time: string, offset: number } = new Date()) {
        // console.warn("LDT: "); console.warn(value);
        let result: moment.Moment = null;
        if (value) {
            if (value instanceof Date) {
                // console.warn("LDT date: " + value);
                result = moment(value);
            } else if (typeof value === 'number') {
                // console.warn("LDT num: " + value);
                result = moment(value);
            } else if (typeof value === "string") {
                if (LocalDateTime.isISODateTime(value)) {
                    // console.warn("LDT str 1: " + value);
                    result = moment.parseZone(value, null, true);
                } else {
                    // console.warn("LDT str 2: " + value);
                    value = value.replace(/\./g, "-"); // Az magyar formátumú dátumot át kell alakítani ISO formátummá (pl: 2022.01.01 -> 2022-01-01)
                    result = moment(value, null, true);
                }
            } else if (moment.isMoment(value)) {
                result = value;
            } else if (LocalDateTime.isLocalDateTime(value)) {
                // console.warn("LDT LDT: " + value.time);
                const tmp: LocalDateTime = <LocalDateTime>value;
                result = moment(tmp.time).utcOffset(tmp.offset, false);
            }
        } else {
            // console.warn("LDT null: " + value);
        }

        if (result && moment.isMoment(result) && (<moment.Moment>result).isValid()) {
            this.time = result.toISOString(false);
            this.offset = result.utcOffset();
        } else {
            this.time = null;
            this.offset = null;
        }
        // console.warn("LDT result - time:  " + this.time + "; offset: " + this.offset);
    }

    /**
     * Visszaadja az osztály 'time' mezőjének az értékét.
     * 
     * @returns 
     */
    public getTime(): string {
        return this.time;
    }

    /**
     * Visszaadja az osztály 'offset' mezőjének az értékét.
     * 
     * @returns 
     */
    public getOffset(): number {
        return this.offset;
    }

    /**
     * Megvizsgálja, hogy a jelenlegi objektum és a paraméterben megadott egyenlő-e,
     * azaz paraméterben megadott is LocalDateTime struktúrájú és a 'time' mező értéke
     * megyezezik az ebben az objektumban tárolttal.
     * 
     * @param other 
     * @returns true, ha a paraméterben megadott érték megegyezik a jelenlegivel, 
     *          egyébként false
     */
    public equals(other: any): boolean {
        return LocalDateTime.isLocalDateTime(other)
            && (<LocalDateTime>other).time === this.time;
    }

    /**
     * Összehasonlítja a megadott két értéket. Eredményként egy számértékkel 
     * tér vissza, aminek értéke lehet:
     *  <0: value1 korábbi, mint value2
     *  =0: value1 és value2 egyező időpontok, vagy az összehasonlítás nem 
     *        végezhető el (pl: érvénytelen típus miatt; pl. Array.sort() esetén
     *        ennek hatására nem változik a két elem helyzete a rendezésben);
     *  >0: value1 későbbi, mint value2
     * 
     * @param value1 
     * @param value2 
     * @returns 
     */
    public static compare(value1: LocalDateTime, value2: LocalDateTime): number {
        let result: number = 0;
        if (!value1 || !value2 || value1 === value2) {
            result = 0;
        } else {
            const ldt1: LocalDateTime = new LocalDateTime(value1);
            const ldt2: LocalDateTime = new LocalDateTime(value2);
            if (!ldt1.isValid() || !ldt2.isValid()) {
                result = 0;
            } else {
                result = ldt1.toDate().getTime() - ldt2.toDate().getTime();
            }
        }
        return result;
    }

    /**
     * Összehaonslítja a jelenlegi objektumot a paraméterben megadottal.
     * 
     * @see LocalDateTime.compare
     * 
     * @param other 
     * @returns <0: a jelenlegi időértéke korábbi, mint a paraméteré; 
     *          =0: a kettő időértéke egyenlő, vagy nem határozható meg; 
     *          >0: a paraméterben megadotté a későbbi.
     */
    public compareTo(other): number {
        return LocalDateTime.compare(this, other);
    }

    /**
     * Az aktuális objektum értékei alapján egy JSON string-et gyárt.
     * @returns 
     */
    public toJSON(): string {
        return JSON.stringify({
            time: this.time,
            offset: this.offset
        });
    }

    /**
     * Visszaadja a jelenlegi objektum időpont-értékét javascript Date objektumban. 
     * FONTOS!! Ez a futás helyének megfelelő időzónába kerül (akár szerver-, akár böngésző-oldali a hívás)!!
     * 
     * Ha érvénytelen a tárolt érték, akkor null-lal tér vissza!
     * 
     * @returns 
     */
    public toDate(): Date {
        const mmnt = this.toMoment();
        return mmnt ? mmnt.toDate() : null;
    }

    /**
     * Visszaadja a jelenlegi objektum időpont-értékét Moment objektumban. 
     * Az időzóna az offset mezőben beállítottnak megelfelő!
     * 
     * Ha érvénytelen a tárolt érték, akkor null-lal tér vissza!
     * 
     * @returns 
     */
    public toMoment(): moment.Moment {
        if (!this.isValid()) {
            return null;
        }
        const mmnt: moment.Moment = moment(this.time).utcOffset(this.offset, true);
        return mmnt.isValid() ? mmnt : null;
    }

    /**
     * Visszaadja, hogy a tárolt időpont-érték érvényes/valós-e.
     * 
     * @returns 
     */
    public isValid(): boolean {
        return LocalDateTime.isLocalDateTime(this)
            && LocalDateTime.isUTCDateTime(this.time);
    }

    /**
     * Megadja, hogy paraméterként átadott időpont-érték ELŐTTI-e 
     * a jelenlegi objektumban beállított időpont.
     * 
     * @param other 
     * @returns 
     */
    public isBefore(other: any): boolean {
        if (!other) {
            return false;
        }
        if (!(other instanceof LocalDateTime)) {
            other = new LocalDateTime(other);
            if (!other.isValid()) {
                return false;
            }
        }
        const mmnt = this.toMoment();
        return mmnt ? mmnt.isBefore((<LocalDateTime>other).toMoment()) : false;
    }
    /**
     * Megadja, hogy paraméterként átadott időpont-érték ELŐTTI-e, 
     * vagy azzal megegyezik a jelenlegi objektumban beállított időpont.
     * 
     * @param other 
     * @returns 
     */
    public isSameOrBefore(other: any): boolean {
        if (!other) {
            return false;
        }
        if (!(other instanceof LocalDateTime)) {
            other = new LocalDateTime(other);
            if (!other.isValid()) {
                return false;
            }
        }
        const mmnt = this.toMoment();
        return mmnt ? mmnt.isSameOrBefore((<LocalDateTime>other).toMoment()) : false;
    }
    /**
     * Megadja, hogy paraméterként átadott időpont-érték UTÁNI-e 
     * a jelenlegi objektumban beállított időpont.
     * 
     * @param other 
     * @returns 
     */
    public isAfter(other: any): boolean {
        if (!other) {
            return false;
        }
        if (!(other instanceof LocalDateTime)) {
            other = new LocalDateTime(other);
            if (!other.isValid()) {
                return false;
            }
        }
        const mmnt = this.toMoment();
        return mmnt ? mmnt.isAfter((<LocalDateTime>other).toMoment()) : false;
    }
    /**
     * Megadja, hogy paraméterként átadott időpont-érték UTÁNI-e, 
     * vagy azzal megegyezik a jelenlegi objektumban beállított időpont.
     * 
     * @param other 
     * @returns 
     */
    public isSameOrAfter(other: any): boolean {
        if (!other) {
            return false;
        }
        if (!(other instanceof LocalDateTime)) {
            other = new LocalDateTime(other);
            if (!other.isValid()) {
                return false;
            }
        }
        const mmnt = this.toMoment();
        return mmnt ? mmnt.isSameOrAfter((<LocalDateTime>other).toMoment()) : false;
    }

    /**
     * Hozzáadja a megadott értéket a megadott mértékkel az aktuáis időponthoz, 
     * majd visszatér egy ÚJ LocalDateTime objektummal.
     * 
     * @param value 
     * @param unit 
     * @returns 
     */
    public add(value: DurationInputArg1, unit: DurationInputArg2): LocalDateTime {
        const mmnt = this.toMoment();
        return mmnt ? new LocalDateTime(mmnt.add(value, unit)) : null;
    }
    /**
     * Kivonja a megadott értéket a megadott mértékkel az aktuáis időpontból, 
     * majd visszatér egy ÚJ LocalDateTime objektummal.
     * 
     * @param value 
     * @param unit 
     * @returns 
     */
    public subtract(value: DurationInputArg1, unit: DurationInputArg2): LocalDateTime {
        const mmnt = this.toMoment();
        return mmnt ? new LocalDateTime(mmnt.subtract(value, unit)) : null;
    }

    /**
     * Az aktuális időpont szövegesen formázott változatával tér vissza a paraméterekben 
     * megadott beállításoknak megfelelően.
     * 
     * @param value
     *  - string: A formázószöveget lehet így megadni (ld. lejjebb).
     *  - number: Az időzóna-etolást lehet így megadni (percben).
     *  - objektum:
     *      + locale: A megjelenítés lokalizációja (ha nincs megadva, akkor magyar 
     *                lokalizáció lesz alkalmazva)
     *      + offset: Az időzóna-eltolás értéke (percben vagy +-óó:pp formában). 
     *                Ha nincs megadva, akkor a hívás helyének(!) megfelelő időzónabeállítás 
     *                lesz alkalmazva.
     *      + format: A formázószöveg. Ha nincs megadva, akkor a lokalizációnak megfelelő 
     *                formátum kerül alkalmazása.
     *                @see: https://momentjs.com/docs/#/displaying/
     * 
     * Paraméter nélkül magyar dátum- és időformátumban jelenik meg a tárolt időpont 
     * a hívás helyének megfelelő időzónában.
     * pl. "2021.01.01 01:01:01"
     * 
     * Ha érvénytelen a tárolt időpont, akkor null-lal tér vissza.
     * 
     * @returns 
     */
    public format(value?: string | number | { locale?: string, offset?: string | number, format?: string }): string {
        if (!this.isValid()) {
            return null;
        }

        let locale: string = null;
        let offset: number | string = null;
        let format: string = null;
        if (value) {
            if (typeof value === 'number') {
                offset = value;
            } else if (typeof value === 'string') {
                format = value;
            } else {
                if (value.locale) locale = value.locale;
                if (value.offset) offset = value.offset;
                if (value.format) format = value.format;
            }
        }

        let result: moment.Moment = this.toMoment();
        if (!result) {
            return null;
        }
        result = result.locale(locale ? locale : "hu-HU");

        if (offset) {
            // Ha az offset szövegként érkezik, akkor is szám lesz így (az időzóna-eltérés ideje percben).
            offset = moment().utcOffset(offset).utcOffset();
            result.utcOffset(offset, true);
        }

        if (format) {
            return result.format(format);
        } else {
            return result.format("L") + " " + result.format("LTS");
        }
    }

    /**
     * Az aktuális időpont dátum részének szövegesen formázott változatával tér vissza a paraméterekben megadott beállításoknak megfelelően.
     * 
     * Paraméter nélkül magyar dátumfomátumban jelenik meg a tárolt időpont. n. pl. "2021.01.01"
     * 
     * @param value 
     * @returns 
     */
    public toDateString(value?: { locale?: string, offset?: string | number }): string {
        return this.format({
            format: "L",
            locale: (value ? value.locale : null),
            offset: (value ? value.offset : null)
        });
    }

    /**
     * Az aktuális időpont idő részének szövegesen formázott változatával tér vissza a paraméterekben megadott beállításoknak megfelelően.
     * 
     * Paraméter nélkül magyar időformában jelenik meg a tárolt időpont a hívás helyének megfelelő időzónában. pl. "01:01:01"
     * 
     * @param value 
     * @returns 
     */
    public toTimeString(value?: { locale?: string, offset?: string | number }): string {
        return this.format({
            format: "LTS",
            locale: (value ? value.locale : null),
            offset: (value ? value.offset : null)
        });
    }

    /**
     * Az aktuális időpont szövegesen formázott változatával tér vissza UTC(!) időzonában.
     * 
     * @param value 
     * @returns 
     */
    public toUTCString(): string {
        return this.format({ offset: "+00:00" });
    }

    /**
     * Az aktuális időpont szövegesen formázott változatával tér vissza.
     * 
     * @see LocalDateTime.format
     * 
     * @param value 
     * @returns 
     */
    public toString(): string {
        return this.format();
    }

    /**
     * Az aktuális időpont szövegesen ISO-formázott szövegével tér vissza 
     * a hívás helyének(!) megfelelő időzónában.
     * 
     * @returns 
     */
    public toLocalISOString(offset: number): string {
        let mmnt = this.toMoment();
        mmnt.utcOffset(offset, true);
        return mmnt ? mmnt.toISOString(true) : null;
    }
}